import { Color, Filter, GlProgram, UniformGroup } from 'pixi.js';

const vertex = `
  in vec2 aPosition;
  out vec2 vTextureCoord;

  uniform vec4 uInputSize;
  uniform vec4 uOutputFrame;
  uniform vec4 uOutputTexture;

  vec4 filterVertexPosition( void )
  {
      vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;

      position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
      position.y = position.y * (2.0*uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;

      return vec4(position, 0.0, 1.0);
  }

  vec2 filterTextureCoord( void )
  {
      return aPosition * (uOutputFrame.zw * uInputSize.zw);
  }

  void main(void)
  {
      gl_Position = filterVertexPosition();
      vTextureCoord = filterTextureCoord();
  }
`;

const fragment = `
  in vec2 vTextureCoord;
  uniform sampler2D uTexture;
  uniform vec3 uPrimaryColour;
  uniform vec3 uSecondaryColour;

  void main(void)
  {
      vec4 texColor = texture2D(uTexture, vTextureCoord);
      
      // Red channel -> Primary colour
      vec3 primary = uPrimaryColour * texColor.r;
      
      // Green channel -> Secondary colour
      vec3 secondary = uSecondaryColour * texColor.g;
      
      // Blue channel -> Fixed/untinted (preserving whites/teeth)
      vec3 fixedColor = vec3(texColor.b);
      
      // Combine all channels
      // Note: we sum them up. The assets are designed such that channels are mostly exclusive
      // for tintable regions, or additive for fixed regions.
      gl_FragColor = vec4(primary + secondary + fixedColor, texColor.a);
  }
`;

/**
 * A custom filter that swaps the Red and Green channels of a texture for specific colors,
 * while leaving the Blue channel as a fixed "pass-through" color (e.g. for whites or teeth).
 */
export class RgbChannelSwapFilter extends Filter {
  private _primaryUniforms: UniformGroup<{
    uPrimaryColour: { value: number[]; type: 'vec3<f32>' };
    uSecondaryColour: { value: number[]; type: 'vec3<f32>' };
  }>;

  constructor() {
    const primaryUniforms = new UniformGroup({
      uPrimaryColour: { value: [1, 1, 1], type: 'vec3<f32>' },
      uSecondaryColour: { value: [1, 1, 1], type: 'vec3<f32>' },
    });

    super({
      glProgram: GlProgram.from({
        fragment,
        vertex,
      }),
      resources: {
        primaryUniforms,
      },
    });

    this._primaryUniforms = primaryUniforms;
  }

  /**
   * Sets the primary color (mapped from the Red channel).
   * @param hex - Hexadecimal color value (e.g. 0xFF0000)
   */
  setPrimaryColour(hex: number): void {
    const color = new Color(hex);
    const rgb = color.toRgbArray();
    this._primaryUniforms.uniforms.uPrimaryColour = [rgb[0], rgb[1], rgb[2]];
  }

  /**
   * Sets the secondary color (mapped from the Green channel).
   * @param hex - Hexadecimal color value (e.g. 0x00FF00)
   */
  setSecondaryColour(hex: number): void {
    const color = new Color(hex);
    const rgb = color.toRgbArray();
    this._primaryUniforms.uniforms.uSecondaryColour = [rgb[0], rgb[1], rgb[2]];
  }

  get primaryColour(): number {
    const rgb = this._primaryUniforms.uniforms.uPrimaryColour;
    return new Color([rgb[0], rgb[1], rgb[2]]).toNumber();
  }

  set primaryColour(hex: number) {
    this.setPrimaryColour(hex);
  }

  get secondaryColour(): number {
    const rgb = this._primaryUniforms.uniforms.uSecondaryColour;
    return new Color([rgb[0], rgb[1], rgb[2]]).toNumber();
  }

  set secondaryColour(hex: number) {
    this.setSecondaryColour(hex);
  }
}
